import type {
  CreateLocationRequest,
  Location,
  UpdateLocationRequest,
} from '../../api/location.service';
import type { DemoStoreData } from '../store-types';
import { route, type HandlerEntry } from '../handler-registry';

function newId(data: DemoStoreData): string {
  return `loc-${data.locations.length + 1}-${Date.now().toString(36).slice(-4)}`;
}

export const locationsHandlers: readonly HandlerEntry[] = [
  route('GET', /^\/api\/tenants\/([^/]+)\/locations$/, (_ctx, data) => data.locations),
  route('GET', /^\/api\/tenants\/([^/]+)\/locations\/([^/]+)$/, (ctx, data) => {
    const id = decodeURIComponent(ctx.match[2]);
    const found = data.locations.find((l) => l.locationId === id);
    return found ?? { error: { status: 404, message: 'Not found' } };
  }),
  route('POST', /^\/api\/tenants\/([^/]+)\/locations$/, (ctx) => {
    const body = ctx.body as CreateLocationRequest;
    return {
      mutate: (data: DemoStoreData) => {
        const location: Location = {
          locationId: newId(data),
          tenantId: data.tenant.tenantId,
          name: body.name,
          description: body.description ?? null,
          address: body.address ?? null,
          phone: body.phone ?? null,
          email: body.email ?? null,
          capacity: body.capacity ?? null,
          notes: body.notes ?? null,
          status: 'Active',
          createdAt: new Date().toISOString(),
          updatedAt: null,
        };
        return {
          next: { ...data, locations: [...data.locations, location] },
          result: location,
        };
      },
    };
  }),
  route('PATCH', /^\/api\/tenants\/([^/]+)\/locations\/([^/]+)$/, (ctx) => {
    const id = decodeURIComponent(ctx.match[2]);
    const body = ctx.body as UpdateLocationRequest;
    return {
      mutate: (data: DemoStoreData) => {
        const locations = data.locations.map((l) =>
          l.locationId === id
            ? {
                ...l,
                name: body.name ?? l.name,
                description:
                  body.description === undefined ? l.description : body.description,
                address: body.address === undefined ? l.address : body.address,
                phone: body.phone === undefined ? l.phone : body.phone,
                email: body.email === undefined ? l.email : body.email,
                capacity: body.capacity === undefined ? l.capacity : body.capacity,
                notes: body.notes === undefined ? l.notes : body.notes,
                status: body.status ?? l.status,
                updatedAt: new Date().toISOString(),
              }
            : l,
        );
        const result = locations.find((l) => l.locationId === id)!;
        return { next: { ...data, locations }, result };
      },
    };
  }),
  route('DELETE', /^\/api\/tenants\/([^/]+)\/locations\/([^/]+)$/, (ctx) => {
    const id = decodeURIComponent(ctx.match[2]);
    return {
      mutate: (data: DemoStoreData) => ({
        next: {
          ...data,
          locations: data.locations.filter((l) => l.locationId !== id),
        },
        result: undefined as void,
      }),
    };
  }),
];
